import React, { useState, useEffect, useCallback } from 'react';
import { orders as ordersApi } from '../utils/api';
import { useT } from '../i18n/I18nContext';

// Ícone e rótulo por tipo de evento. Tipos desconhecidos (de versões futuras)
// caem no genérico em vez de sumir da lista.
const EVENT_META = {
  order_created:       { icon: '📦', label: 'Pedido criado' },
  bulk_generated:      { icon: '🔵', label: 'Bulk gerado' },
  status_changed:      { icon: '🔄', label: 'Status' },
  payment_added:       { icon: '💰', label: 'Pagamento' },
  payment_updated:     { icon: '✏️', label: 'Pagamento editado' },
  payment_deleted:     { icon: '🗑️', label: 'Pagamento excluído' },
  delivery_updated:    { icon: '📅', label: 'Prazo/entrega' },
  item_prices_updated: { icon: '🏷️', label: 'Preços' },
  document_added:      { icon: '📎', label: 'Documento' },
};
const fallback = { icon: '•', label: 'Alteração' };

// Data/hora vinda do SQLite ('YYYY-MM-DD HH:MM:SS', em UTC)
function fmtWhen(s) {
  if (!s) return '—';
  const iso = String(s).replace(' ', 'T') + (String(s).endsWith('Z') ? '' : 'Z');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Linha "campo: antes → depois" a partir do meta do evento
function DiffLines({ meta }) {
  if (!meta || !meta.antes || !meta.depois) return null;
  const chaves = [...new Set([...Object.keys(meta.antes), ...Object.keys(meta.depois)])];
  const mudou = chaves.filter(k => JSON.stringify(meta.antes[k]) !== JSON.stringify(meta.depois[k]));
  if (!mudou.length) return null;
  const show = v => (v === null || v === undefined || v === '' ? '—' : String(v));
  return (
    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {mudou.map(k => (
        <div key={k} style={{ fontSize: 11, color: 'var(--gray-500)', fontFamily: 'var(--font-mono)' }}>
          {k}: <span style={{ textDecoration: 'line-through', opacity: .6 }}>{show(meta.antes[k])}</span>
          {' → '}<strong style={{ color: 'var(--pantone)' }}>{show(meta.depois[k])}</strong>
        </div>
      ))}
    </div>
  );
}

// Histórico de movimentações e alterações do pedido: quem mexeu, no quê e quando.
export default function OrderHistory({ quotation: q, refreshKey }) {
  const t = useT();
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ordersApi.events(q.id)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [q.id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const list = events || [];
  const visible = expanded ? list : list.slice(0, 8);

  return (
    <div className="card no-print" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)' }}>
          🕘 {t('history.title')}{list.length ? ` (${list.length})` : ''}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading} title={t('history.reload')}>
          {loading ? '⏳' : '🔄'}
        </button>
      </div>

      {loading && !events ? (
        <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>⏳ {t('common.loading')}</div>
      ) : list.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>{t('history.empty')}</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {visible.map((e, i) => {
              const meta = EVENT_META[e.event_type] || fallback;
              return (
                <div key={e.id} style={{
                  display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 10, padding: '9px 0',
                  borderBottom: i < visible.length - 1 ? '1px solid var(--gray-100)' : 'none',
                }}>
                  <div style={{ fontSize: 16, lineHeight: '18px' }} title={meta.label}>{meta.icon}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--gray-700)' }}>{e.description || meta.label}</div>
                    <DiffLines meta={e.meta} />
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{fmtWhen(e.created_at)}</div>
                    {e.user_name && <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{e.user_name}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          {list.length > 8 && (
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setExpanded(v => !v)}>
              {expanded ? t('history.show_less') : `${t('history.show_all')} (${list.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
